import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

const WeekContext = createContext(undefined);

/**
 * WeekProvider – Provides current-week data and helpers to child components.
 *
 * Manages the active week state including:
 * - Week metadata (id, title, slug, description)
 * - Children content (video, checklist, recipesList, resourcesList)
 * - Filtered access to children by type
 */
export const WeekProvider = ({ children, initialWeek = null }) => {
  const [week, setWeek] = useState(initialWeek);

  // Sync state when initialWeek prop changes (e.g., after async fetch completes)
  useEffect(() => {
    setWeek(initialWeek);
  }, [initialWeek]);

  // ── Derived child content ───────────────────────────────────────
  const video = useMemo(() => {
    return week?.Children?.find(child => child.type === 'video') || null;
  }, [week]);

  const checklist = useMemo(() => {
    return week?.Children?.find(child => child.type === 'checklist') || null;
  }, [week]);

  const recipesList = useMemo(() => {
    return week?.Children?.find(child => child.type === 'recipesList') || null;
  }, [week]);

  const resourcesList = useMemo(() => {
    return week?.Children?.find(child => child.type === 'resourcesList') || null;
  }, [week]);

  const allContent = useMemo(() => {
    return week?.Children || [];
  }, [week]);

  // ── Helpers ─────────────────────────────────────────────────────
  const getContentByType = useCallback(
    type => {
      if (!week?.Children) return [];
      return week.Children.filter(child => child.type === type);
    },
    [week]
  );

  const findContentBySlug = useCallback(
    slug => {
      if (!week?.Children) return null;
      return week.Children.find(child => child.slug === slug);
    },
    [week]
  );

  const findContentById = useCallback(
    id => {
      if (!week?.Children) return null;
      return week.Children.find(child => child.id === id);
    },
    [week]
  );

  // ── Metadata ────────────────────────────────────────────────────
  const metadata = useMemo(() => {
    if (!week) return null;
    return {
      id: week.id,
      name: week.name,
      title: week.title,
      slug: week.slug,
      description: week.displayProps?.description || week.data?.description,
      image: week.displayProps?.image,
      type: week.type,
      published: week.published,
      free: week.free,
      createdAt: week.createdAt
    };
  }, [week]);

  const value = useMemo(
    () => ({
      // Core week data
      week,
      setWeek,

      // Metadata
      metadata,

      // Pre-filtered children
      video,
      checklist,
      recipesList,
      resourcesList,
      allContent,

      // Helpers
      getContentByType,
      findContentBySlug,
      findContentById,

      // Convenience flags
      hasWeek: !!week,
      isLoading: week === undefined
    }),
    [
      week,
      metadata,
      video,
      checklist,
      recipesList,
      resourcesList,
      allContent,
      getContentByType,
      findContentBySlug,
      findContentById
    ]
  );

  return <WeekContext.Provider value={value}>{children}</WeekContext.Provider>;
};

WeekProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialWeek: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    published: PropTypes.bool,
    free: PropTypes.bool,
    createdAt: PropTypes.string,
    displayProps: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string
      })
    }),
    data: PropTypes.shape({
      description: PropTypes.string
    }),
    Children: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

/**
 * useWeek – Consumer hook for week context.
 *
 * @returns {Object} Week context value
 * @throws {Error} If used outside a WeekProvider
 */
export const useWeek = () => {
  const context = useContext(WeekContext);

  if (context === undefined) {
    throw new Error('useWeek must be used within a WeekProvider');
  }

  return context;
};

/**
 * withWeek – HOC to inject week context as props.
 * Useful for class components that cannot use hooks directly.
 */
export const withWeek = Component => {
  const WrappedComponent = props => {
    const weekContext = useWeek();
    return <Component {...props} week={weekContext} />;
  };

  WrappedComponent.displayName = `withWeek(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

export default WeekContext;
